import { useState } from 'react';
import { Check, MapPin, Mail, Send } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ScrollReveal from '@/components/ScrollReveal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industryOptions = [
  'Energy & Utilities', 'Real Estate & Construction', 'Financial Institutions',
  'Infrastructure', 'Transport & Logistics', 'Technology', 'Healthcare',
  'Government / Sovereign', 'Other',
];

const bondTypeOptions = [
  'Conventional Fixed-Rate Bond', 'Floating Rate Note (FRN)', 'Green / ESG Bond',
  'Sukuk (Islamic Bond)', 'Sustainability-Linked Bond', 'Private Placement',
  'Project Finance Bond', 'Not Sure - Need Advisory',
];

const sizeOptions = [
  'Under $10M', '$10M - $50M', '$50M - $250M',
  '$250M - $500M', '$500M - $1B', 'Over $1B',
];

const jurisdictionOptions = [
  'DIFC (Dubai)', 'ADGM (Abu Dhabi)', 'Both / Flexible', 'Other',
];

const timelineOptions = [
  'Immediate (1-3 months)', 'Near-term (3-6 months)',
  'Medium-term (6-12 months)', 'Exploratory',
];

export default function ContactForm() {
  const [form, setForm] = useState({
    full_name: '', title_position: '', organization: '', industry: '',
    email: '', phone: '', bond_type: '', estimated_size: '',
    preferred_jurisdiction: '', timeline: '', additional_details: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Required';
    if (!form.title_position.trim()) e.title_position = 'Required';
    if (!form.organization.trim()) e.organization = 'Required';
    if (!form.industry) e.industry = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    if (!form.bond_type) e.bond_type = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
    } catch (err) {
      console.error('Contact submit error:', err);
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <section id="contact" className="bg-white py-20 sm:py-28">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <ScrollReveal>
            <div
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: '#2d8a56' }}
              data-testid="contact-success-icon"
            >
              <Check className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-playfair font-bold text-2xl mb-3" style={{ color: '#0a1628' }}>
              Inquiry Submitted Successfully
            </h3>
            <p className="font-inter" style={{ color: '#6b7280' }}>
              Our Debt Capital Markets team will respond within 48 hours. All inquiries are treated with strict confidentiality.
            </p>
          </ScrollReveal>
        </div>
      </section>
    );
  }

  const inputCls = (field) =>
    `w-full px-4 py-2.5 rounded font-inter text-sm outline-none transition-colors border ${
      errors[field] ? 'border-red-400' : 'border-gray-300'
    } focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]`;

  const selectTriggerCls = (field) =>
    `w-full h-[42px] rounded font-inter text-sm border ${
      errors[field] ? 'border-red-400' : 'border-gray-300'
    } focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c] bg-white`;

  return (
    <section id="contact" className="bg-white py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left Column - Info */}
          <div className="lg:col-span-2">
            <ScrollReveal>
              <h3 className="font-playfair font-bold text-2xl sm:text-3xl mb-4" style={{ color: '#0a1628' }}>
                Begin Your Bond Issuance
              </h3>
              <p className="font-inter text-base leading-relaxed mb-10" style={{ color: '#6b7280' }}>
                Complete the inquiry form and our Debt Capital Markets team will respond within 48 hours.
              </p>

              <div className="space-y-6">
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#c9a84c' }} />
                  <div>
                    <p className="font-inter font-semibold text-sm" style={{ color: '#0a1628' }}>DIFC Office</p>
                    <p className="font-inter text-sm" style={{ color: '#6b7280' }}>Gate District, DIFC, Dubai, UAE</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#c9a84c' }} />
                  <div>
                    <p className="font-inter font-semibold text-sm" style={{ color: '#0a1628' }}>ADGM Office</p>
                    <p className="font-inter text-sm" style={{ color: '#6b7280' }}>Al Maryah Island, Abu Dhabi, UAE</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#c9a84c' }} />
                  <div>
                    <p className="font-inter text-sm" style={{ color: '#6b7280' }}>info@alcbond.com</p>
                    <p className="font-inter text-sm" style={{ color: '#6b7280' }}>dcm@alcbond.com</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-3">
            <ScrollReveal delay={100}>
              <div
                className="rounded-xl p-6 sm:p-8"
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                }}
              >
                <h4 className="font-playfair font-semibold text-xl mb-1" style={{ color: '#0a1628' }}>
                  Issuance Inquiry
                </h4>
                <p className="font-inter text-sm mb-6" style={{ color: '#6b7280' }}>
                  All inquiries are treated with strict confidentiality.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  {/* Row 1 */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Full Name *</label>
                      <input
                        className={inputCls('full_name')}
                        value={form.full_name}
                        onChange={(e) => set('full_name', e.target.value)}
                        data-testid="input-full-name"
                      />
                      {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Title / Position *</label>
                      <input
                        className={inputCls('title_position')}
                        value={form.title_position}
                        onChange={(e) => set('title_position', e.target.value)}
                        data-testid="input-title-position"
                      />
                      {errors.title_position && <p className="text-red-500 text-xs mt-1">{errors.title_position}</p>}
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Organization *</label>
                      <input
                        className={inputCls('organization')}
                        value={form.organization}
                        onChange={(e) => set('organization', e.target.value)}
                        data-testid="input-organization"
                      />
                      {errors.organization && <p className="text-red-500 text-xs mt-1">{errors.organization}</p>}
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Industry *</label>
                      <Select value={form.industry} onValueChange={(v) => set('industry', v)}>
                        <SelectTrigger className={selectTriggerCls('industry')} data-testid="select-industry">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industryOptions.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.industry && <p className="text-red-500 text-xs mt-1">{errors.industry}</p>}
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Email *</label>
                      <input
                        type="email"
                        className={inputCls('email')}
                        value={form.email}
                        onChange={(e) => set('email', e.target.value)}
                        data-testid="input-email"
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Phone</label>
                      <input
                        type="tel"
                        className={inputCls('phone')}
                        value={form.phone}
                        onChange={(e) => set('phone', e.target.value)}
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Bond Type *</label>
                      <Select value={form.bond_type} onValueChange={(v) => set('bond_type', v)}>
                        <SelectTrigger className={selectTriggerCls('bond_type')} data-testid="select-bond-type">
                          <SelectValue placeholder="Select bond type" />
                        </SelectTrigger>
                        <SelectContent>
                          {bondTypeOptions.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.bond_type && <p className="text-red-500 text-xs mt-1">{errors.bond_type}</p>}
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Estimated Size (USD)</label>
                      <Select value={form.estimated_size} onValueChange={(v) => set('estimated_size', v)}>
                        <SelectTrigger className={selectTriggerCls('estimated_size')} data-testid="select-estimated-size">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeOptions.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 5 */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Preferred Jurisdiction</label>
                      <Select value={form.preferred_jurisdiction} onValueChange={(v) => set('preferred_jurisdiction', v)}>
                        <SelectTrigger className={selectTriggerCls('preferred_jurisdiction')} data-testid="select-jurisdiction">
                          <SelectValue placeholder="Select jurisdiction" />
                        </SelectTrigger>
                        <SelectContent>
                          {jurisdictionOptions.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Timeline</label>
                      <Select value={form.timeline} onValueChange={(v) => set('timeline', v)}>
                        <SelectTrigger className={selectTriggerCls('timeline')} data-testid="select-timeline">
                          <SelectValue placeholder="Select timeline" />
                        </SelectTrigger>
                        <SelectContent>
                          {timelineOptions.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 6 */}
                  <div>
                    <label className="block font-inter text-xs font-medium mb-1.5" style={{ color: '#374151' }}>Additional Details</label>
                    <textarea
                      rows={4}
                      className="w-full px-4 py-2.5 rounded font-inter text-sm outline-none border border-gray-300 focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c] resize-none"
                      value={form.additional_details}
                      onChange={(e) => set('additional_details', e.target.value)}
                      data-testid="input-additional-details"
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full font-inter font-semibold py-3 rounded flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ backgroundColor: '#c9a84c', color: '#060f1d', transition: 'background-color 200ms' }}
                    onMouseEnter={(e) => { if (!submitting) e.target.style.backgroundColor = '#dfc26e'; }}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#c9a84c'}
                    data-testid="contact-form-submit"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? 'Submitting...' : 'Submit Confidential Inquiry'}
                  </button>

                  <p className="text-center font-inter text-xs" style={{ color: '#9ca3af' }}>
                    Your information is protected under our strict confidentiality protocols.
                  </p>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}
